import { useEffect, useState } from 'react';

import { Reveal } from '../components/effects/Reveal';
import { Spotlight } from '../components/effects/Spotlight';
import { subscribeToMetrics } from '../firebase/metricsService';
import { useCountUp } from '../hooks/useCountUp';
import { captureException } from '../lib/monitoring';
import type { Metric } from '../types/domain';

type Status = 'loading' | 'ready' | 'error';

interface MetricCardProps {
  m: Metric;
  index: number;
}

function MetricCard({ m, index }: MetricCardProps) {
  const { ref, display } = useCountUp(m.value, { duration: 1600 });

  return (
    <Spotlight
      className="group relative h-full rounded-[18px] bg-white/[0.04] border border-white/10 overflow-hidden hover:-translate-y-2 hover:bg-white/[0.07] hover:border-etribe-yellow/30 transition-all duration-400"
      color="rgba(255, 200, 54, 0.18)"
      size={520}
    >
      <article className="relative p-10 lg:p-12 h-full flex flex-col items-center text-center">
        {/* Número gigante de fondo */}
        <span
          aria-hidden
          className="absolute -top-6 -right-2 font-heading font-black text-[180px] leading-none text-white/[0.04] group-hover:text-etribe-yellow/[0.08] transition-colors duration-500 select-none pointer-events-none"
        >
          {String(index + 1).padStart(2, '0')}
        </span>

        {/* Glow amarillo decorativo en hover (esquina inferior) */}
        <span
          aria-hidden
          className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-56 h-56 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(255,200,54,.18), transparent 70%)' }}
        />

        {/* Eyebrow numerada */}
        <div className="relative z-10 inline-flex items-center gap-2 mb-6">
          <span className="font-heading text-[10px] tracking-[0.18em] text-etribe-yellow font-bold">
            {String(index + 1).padStart(2, '0')}
          </span>
          <span className="w-5 h-px bg-etribe-yellow" />
          <span className="font-heading text-[10px] tracking-[0.18em] uppercase text-white/50">
            Métrica
          </span>
        </div>

        {/* Pulse rings detrás del número */}
        <div className="relative z-10 mb-5">
          <div aria-hidden className="absolute inset-0 -m-6 rounded-full border border-etribe-yellow/15 animate-pulse-ring pointer-events-none" />
          <div aria-hidden className="absolute inset-0 -m-3 rounded-full border border-etribe-yellow/10 animate-pulse-ring [animation-delay:1.4s] pointer-events-none" />

          {/* Número grande animado */}
          <div className="relative font-heading font-bold text-[clamp(56px,6vw,88px)] leading-none text-etribe-yellow tracking-[-0.01em] inline-flex items-baseline justify-center group-hover:scale-[1.04] transition-transform duration-400">
            {m.prefix && (
              <span className="text-[.55em] mr-1 text-white/85">{m.prefix}</span>
            )}
            <span ref={ref}>{display}</span>
            {m.suffix && (
              <span className="text-[.5em] ml-1.5 text-white/85">{m.suffix}</span>
            )}
          </div>
        </div>

        {/* Divider amarillo animado */}
        <span
          aria-hidden
          className="relative z-10 block h-0.5 w-10 bg-etribe-yellow/60 mb-5 group-hover:w-20 group-hover:bg-etribe-yellow transition-all duration-500"
        />

        {/* Label */}
        <div className="relative z-10 text-[15px] text-white/85 leading-[1.5]">
          {m.label}
          {m.labelNote && (
            <>
              {' '}
              <em className="not-italic text-white/55 text-[13px]">{m.labelNote}</em>
            </>
          )}
        </div>
      </article>
    </Spotlight>
  );
}

export default function Metrics() {
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [status, setStatus] = useState<Status>('loading');

  useEffect(() => {
    const unsubscribe = subscribeToMetrics((data, err) => {
      if (err) {
        captureException(err, { section: 'metrics', source: 'subscribeToMetrics' });
        setMetrics([]);
        setStatus('error');
        return;
      }
      setMetrics(data);
      setStatus('ready');
    });
    return () => unsubscribe();
  }, []);

  if (status !== 'ready' || metrics.length === 0) return null;

  return (
    <section className="on-blue bg-etribe-blue text-white py-[120px] relative overflow-hidden">
      {/* === Decoración de fondo === */}

      {/* Radial gradients de marca */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 pointer-events-none opacity-70"
        style={{
          background:
            'radial-gradient(40% 30% at 100% 0%, rgba(255,200,54,.12), transparent 60%), radial-gradient(40% 30% at 0% 100%, rgba(50,127,239,.14), transparent 60%)',
        }}
      />

      {/* Grid pattern sutil */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-40 pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.04) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.04) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse at center, black 35%, transparent 80%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 35%, transparent 80%)',
        }}
      />

      {/* Dots overlay */}
      <div
        aria-hidden
        className="absolute inset-0 -z-10 opacity-25 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(rgba(255,200,54,.4) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
          maskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
          WebkitMaskImage: 'radial-gradient(ellipse at center, black 20%, transparent 70%)',
        }}
      />

      <div className="container-etribe relative">
        <Reveal>
          <div className="max-w-[720px] mb-16">
            <p className="eyebrow"><span>Tracción</span></p>
            <h2 className="text-[clamp(32px,3.6vw,48px)] text-white normal-case mb-4">eTribe en números</h2>
            <p className="text-[clamp(17px,1.3vw,20px)] text-white/75 leading-[1.55]">
              Trabajando con líderes del mercado.
            </p>
          </div>
        </Reveal>

        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-5">
          {metrics.map((m, idx) => (
            <Reveal key={m.id} delay={(idx % 3) * 100}>
              <MetricCard m={m} index={idx} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
